import { fixRefName, sentryReleaseName } from "./utils/github";
import {
  getRuntimeEnvVar,
  getRuntimeEnvVarBoolean,
  logConfigurationState,
} from "./utils/runtime-config";

export interface Config {
  /** `VITE_APP_ENV` — "local" | "dev" | "prod". */
  appEnv: string;
  hosts: {
    app: string;
    capture?: string;
    /** Public canvas share / viewer host (`VITE_SHARE_BASE_URL`). Empty → callers use same-origin. */
    share: string;
    api: string;
    static: string;
    /** Marketing site origin (`VITE_MARKETING_BASE_URL`), e.g. https://www.dev.cohesive.ai. */
    marketing: string;
  };
  devtools: {
    reactQuery: boolean;
  };
  integrations: {
    nango: {
      publicKey: string;
    };
    unified: {
      environment: string;
      workspace: string;
    };
  };
  auth0: {
    audience: string;
    clientId: string;
    domain: string;
  };
  sentry: {
    dsn: string;
    environment: string;
    enabled: boolean;
  };
  launchDarkly: {
    clientSideId: string;
    relayHost: string;
  };
  github: {
    sha: string;
    ref: string;
    refName: string;
    /** Same string as Vite Sentry sourcemap `release.name` and `Sentry.init({ release })`. */
    sentryRelease: string;
  };
  mixpanel: {
    token: string;
    debug: boolean;
    enabled: boolean;
    apiHost: string;
  };
  recaptcha: {
    siteKey: string;
  };
  gtm: {
    id: string;
  };
  statusApiUrl: string;
  tiptap: {
    /**
     * Document server id for Collaboration WebSocket (`wss://{id}.collab.tiptap.cloud`).
     * Matches API `TIPTAP_DOCUMENT_SERVER_ID` via `VITE_TIPTAP_DOCUMENT_SERVER_ID`.
     */
    documentServerId: string;
  };
}

const getConfig = (): Config => {
  // Log configuration state in development for debugging
  if (import.meta.env.DEV) {
    logConfigurationState();
  }

  return {
    appEnv: getRuntimeEnvVar("VITE_APP_ENV") || "",
    hosts: {
      // TODO do this better, likely in the CI job
      app:
        getRuntimeEnvVar("VITE_APP_BASE_URL")?.replace(
          "{pr_number}",
          getRuntimeEnvVar("VITE_GITHUB_PULL_REQUEST_NUMBER") || "",
        ) || "",
      capture: getRuntimeEnvVar("VITE_CAPTURE_BASE_URL") || "",
      share: getRuntimeEnvVar("VITE_SHARE_BASE_URL") || "",
      api: getRuntimeEnvVar("VITE_API_BASE_URL") || "",
      static: getRuntimeEnvVar("VITE_STATIC_BASE_URL") || "",
      marketing: getRuntimeEnvVar("VITE_MARKETING_BASE_URL") || "",
    },
    devtools: {
      reactQuery: getRuntimeEnvVarBoolean("VITE_SHOW_REACT_QUERY_DEVTOOLS"),
    },
    integrations: {
      unified: {
        environment: getRuntimeEnvVar("VITE_UNIFIED_ENVIRONMENT") || "",
        workspace: getRuntimeEnvVar("VITE_UNIFIED_WORKSPACE") || "",
      },
      nango: {
        publicKey: getRuntimeEnvVar("VITE_NANGO_PUBLIC_KEY") || "",
      },
    },
    auth0: {
      audience: getRuntimeEnvVar("VITE_AUTH0_AUDIENCE") || "",
      clientId: getRuntimeEnvVar("VITE_AUTH0_CLIENT_ID") || "",
      domain: getRuntimeEnvVar("VITE_AUTH0_DOMAIN") || "",
    },
    sentry: {
      dsn: getRuntimeEnvVar("VITE_SENTRY_DSN") || "",
      environment: getRuntimeEnvVar("VITE_SENTRY_ENVIRONMENT") || "",
      enabled: getRuntimeEnvVarBoolean("VITE_SENTRY_ENABLED"),
    },
    launchDarkly: {
      clientSideId: getRuntimeEnvVar("VITE_LAUNCHDARKLY_CLIENT_SIDE_ID") || "",
      relayHost: getRuntimeEnvVar("VITE_LAUNCHDARKLY_RELAY_HOST") || "",
    },
    github: (() => {
      const ghSha = getRuntimeEnvVar("VITE_GITHUB_SHA") || "";
      const ghRef = getRuntimeEnvVar("VITE_GITHUB_REF") || "";
      const ghShort = getRuntimeEnvVar("VITE_GITHUB_REF_NAME");
      return {
        sha: ghSha,
        ref: ghRef,
        refName: fixRefName(ghShort) || fixRefName(ghRef),
        sentryRelease: sentryReleaseName(ghShort, ghRef, ghSha),
      };
    })(),
    mixpanel: {
      token: getRuntimeEnvVar("VITE_MIXPANEL_TOKEN") || "",
      debug: getRuntimeEnvVarBoolean("VITE_MIXPANEL_DEBUG"),
      enabled: getRuntimeEnvVarBoolean("VITE_MIXPANEL_ENABLED"),
      apiHost: getRuntimeEnvVar("VITE_MIXPANEL_API_HOST") || "",
    },
    recaptcha: {
      siteKey: getRuntimeEnvVar("VITE_RECAPTCHA_SITE_KEY") || "",
    },
    gtm: {
      id: getRuntimeEnvVar("VITE_GTM_ID") || "",
    },
    statusApiUrl: getRuntimeEnvVar("VITE_STATUS_API_URL") || "",
    tiptap: {
      documentServerId:
        getRuntimeEnvVar("VITE_TIPTAP_DOCUMENT_SERVER_ID") || "",
    },
  };
};

export const config = getConfig();
